import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box, Grid, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TabPanel from '@atlaskit/tabs/tab-panel';
import { token } from '@atlaskit/tokens';

import { CopyButton } from '../../common/ui/copy-button';
import { messages } from '../messages';
import type { ElsOverviewData } from '../types';

import { ExpandableSection } from './expandable-section';
import { Panel } from './panel-wrapper';

const styles = cssMap({
	twoColumnGrid: {
		gridTemplateColumns: '1fr 1fr',
	},
	entityCard: {
		borderRadius: token('radius.small'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
	},
});

type ElsOverviewPanelProps = ElsOverviewData;

const Field = ({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element => (
	<Box>
		<Heading size="xsmall">{label}</Heading>
		<Text>{value ?? 'null'}</Text>
	</Box>
);

export const ElsOverviewPanel: (props: ElsOverviewPanelProps) => React.JSX.Element = ({
	hasElsData,
	elsTraceId,
	detectionMetadata,
	prefilterEntities,
	persons,
}: ElsOverviewPanelProps) => {
	const { formatMessage } = useIntl();

	if (!hasElsData) {
		return (
			<TabPanel>
				<Panel testId="sain-online-debugger-els-overview-panel">
					<Text>{formatMessage(messages.ai_debugger_modal_els_empty_state)}</Text>
				</Panel>
			</TabPanel>
		);
	}

	const displayCacheHit =
		detectionMetadata?.cacheHit === null || detectionMetadata?.cacheHit === undefined
			? 'null'
			: detectionMetadata.cacheHit.toString();

	return (
		<TabPanel>
			<Panel testId="sain-online-debugger-els-overview-panel">
				<Box testId="sain-online-debugger-els-trace-id">
					<Heading size="xsmall">
						{formatMessage(messages.ai_debugger_modal_els_trace_id_subheading)}
					</Heading>
					<Inline space="space.100" alignBlock="start">
						<Text>{elsTraceId ?? 'null'}</Text>
						{elsTraceId && (
							<CopyButton
								copyText={elsTraceId}
								copyLabel={formatMessage(messages.ai_debugger_modal_copy_trace_id_label)}
								copiedLabel={formatMessage(messages.ai_debugger_modal_trace_id_copied_text)}
								testId="sain-online-debugger-copy-els-trace-id-button"
							/>
						)}
					</Inline>
				</Box>

				{detectionMetadata && (
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_detection_metadata_subheading)}
						</Heading>
						<Grid xcss={styles.twoColumnGrid} gap="space.100">
							<Field
								label={formatMessage(messages.ai_debugger_modal_detection_method_label)}
								value={detectionMetadata.method}
							/>
							<Field
								label={formatMessage(messages.ai_debugger_modal_detection_model_label)}
								value={detectionMetadata.model}
							/>
							<Field
								label={formatMessage(messages.ai_debugger_modal_detection_latency_label)}
								value={detectionMetadata.latencyMs}
							/>
							<Field
								label={formatMessage(messages.ai_debugger_modal_detection_cache_hit_label)}
								value={displayCacheHit}
							/>
						</Grid>
					</Box>
				)}

				{prefilterEntities.length > 0 && (
					<ExpandableSection
						title={formatMessage(messages.ai_debugger_modal_prefilter_entities_subheading)}
						testId="sain-online-debugger-prefilter-entities-section"
					>
						<Stack space="space.100">
							{prefilterEntities.map((entity, index) => (
								<Box
									key={entity.id ?? `prefilter-entity-${index}`}
									xcss={styles.entityCard}
									backgroundColor="color.background.neutral"
									testId="sain-online-debugger-prefilter-entity"
								>
									<Text weight="bold">{entity.name}</Text>
									<Grid xcss={styles.twoColumnGrid} gap="space.050">
										<Field
											label={formatMessage(messages.ai_debugger_modal_prefilter_entity_type_label)}
											value={entity.entityType}
										/>
										<Field
											label={formatMessage(
												messages.ai_debugger_modal_prefilter_entity_mention_label,
											)}
											value={entity.mention}
										/>
										<Field
											label={formatMessage(messages.ai_debugger_modal_entity_id_label)}
											value={entity.id}
										/>
										<Field
											label={formatMessage(
												messages.ai_debugger_modal_prefilter_entity_confidence_label,
											)}
											value={entity.confidenceScore}
										/>
										<Field
											label={formatMessage(
												messages.ai_debugger_modal_prefilter_entity_clarification_label,
											)}
											value={entity.clarificationLevel}
										/>
									</Grid>
								</Box>
							))}
						</Stack>
					</ExpandableSection>
				)}

				{persons.length > 0 && (
					<ExpandableSection
						title={formatMessage(messages.ai_debugger_modal_persons_subheading)}
						testId="sain-online-debugger-persons-section"
					>
						<Stack space="space.100">
							{persons.map((person, index) => (
								<Box
									key={person.accountId ?? `person-${index}`}
									xcss={styles.entityCard}
									backgroundColor="color.background.neutral"
									testId="sain-online-debugger-person"
								>
									<Text weight="bold">{person.name ?? 'null'}</Text>
									<Grid xcss={styles.twoColumnGrid} gap="space.050">
										<Field
											label={formatMessage(messages.ai_debugger_modal_person_account_id_label)}
											value={person.accountId}
										/>
										<Field
											label={formatMessage(messages.ai_debugger_modal_person_email_label)}
											value={person.email}
										/>
										<Field
											label={formatMessage(messages.ai_debugger_modal_person_job_title_label)}
											value={person.jobTitle}
										/>
										<Field
											label={formatMessage(messages.ai_debugger_modal_person_department_label)}
											value={person.department}
										/>
										<Field
											label={formatMessage(messages.ai_debugger_modal_person_organization_label)}
											value={person.organization}
										/>
										<Field
											label={formatMessage(messages.ai_debugger_modal_person_location_label)}
											value={person.location}
										/>
										<Field
											label={formatMessage(messages.ai_debugger_modal_person_zone_info_label)}
											value={person.zoneInfo}
										/>
									</Grid>
								</Box>
							))}
						</Stack>
					</ExpandableSection>
				)}
			</Panel>
		</TabPanel>
	);
};
